var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
// React Imports
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { faFlag, faExpandAlt, faCompressAlt } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Store } from 'react-notifications-component';
// Custom Imports
import ExamSidebar from 'components/sidebar/sidebar';
import QuestionDrawer from 'components/question-drawer/question-drawer';
import Button from 'components/button/button';
import { ButtonStyle } from 'components/button/prop-types';
import Loader from 'components/loader/loader';
import RichTextViewer from 'components/rich-text-viewer/rich-text-viewer';
import Timer from 'components/timer/timer';
import ConfirmationModal from 'components/confirmation-modal/confirmation-modal';
import useAuthentication from 'hooks/use-authentication';
import useQueryString from 'hooks/use-querystring';
import { useSubmitExam } from 'hooks/use-submit-exam';
import EXAM_ATTEMPT_QUERY from 'queries/exam-attempt.graphql';
import UPSERT_QESTION_ANSWER from 'mutations/upsert-question-answer.graphql';
import { SideTabContext } from 'context/sidetab-context';
import showNotification from 'notifications/notifications';
import { getQuestionCount, getQuestionIndex, isMiddleScreen, filterDisplayOnExamNavigation } from 'helpers/questions';
import ReviewPage from 'pages/review/reviewV2';
const QuestionBuilder = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showSideBar } = useContext(SideTabContext);
    const { isSubmitting, submitExam } = useSubmitExam();
    const { getExamIdFromToken, getUserIdFromToken } = useAuthentication();
    const queryString = useQueryString();
    const { data: examAttemptData, loading, refetch } = useQuery(EXAM_ATTEMPT_QUERY, {
        variables: {
            userId: getUserIdFromToken(),
            examId: getExamIdFromToken(),
        },
        fetchPolicy: 'no-cache',
    });
    const [upsertQuestionAnswer, { loading: loadingUpsertQuestionAnswer }] = useMutation(UPSERT_QESTION_ANSWER);
    const [exam, setExam] = useState();
    const [questions, setQuestions] = useState([]);
    const [preSelectOnLoadQuestions, setPreselectOnLoadQuestions] = useState(true);
    const [selectedQuestionIndex, setSelectedQuestionIndex] = useState();
    const [showExtendedMenu, setShowExtendedMenu] = useState(false);
    const [marking, setMarking] = useState(false);
    const [, setSaving] = useState();
    const [questionIsDirt, setQuestionIsDirt] = useState(false);
    const [showExpand, setShowExpand] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    useEffect(() => {
        checkSelectedQuestion();
    }, [queryString]);
    useEffect(() => {
        const attemptExam = examAttemptData === null || examAttemptData === void 0 ? void 0 : examAttemptData.examAttempt.exam;
        if (!attemptExam) {
            // Exam data not found on attempt
            return;
        }
        setExam(attemptExam);
        setQuestions(attemptExam.questions);
    }, [examAttemptData]);
    useEffect(() => {
        if (selectedQuestionIndex) {
            return;
        }
        if (questions.length > 0 && preSelectOnLoadQuestions) {
            checkSelectedQuestion();
            setPreselectOnLoadQuestions(false);
        }
    }, [questions]);
    useEffect(() => {
        setShowExpand(false);
    }, [selectedQuestionIndex]);
    const checkSelectedQuestion = () => {
        var _a;
        const preSelectQuestion = queryString.get('question');
        const goNext = () => {
            const next = questions.find((q) => { var _a; return !((_a = q.questionAnswer) === null || _a === void 0 ? void 0 : _a.answered); });
            if (next) {
                nextQuestion(next);
                return;
            }
            else {
                const lastQuestion = questions[questions.length - 1];
                nextQuestion(lastQuestion);
            }
        };
        if (preSelectQuestion && questions.length >= 1) {
            const question = questions.find(question => question.id === +preSelectQuestion);
            if (question) {
                setSelectedQuestionIndex(getIndex(question));
                if (((_a = exam === null || exam === void 0 ? void 0 : exam.examConfigurations) === null || _a === void 0 ? void 0 : _a.allowGoBack) === false) {
                    goNext();
                    return;
                }
                return;
            }
            else {
                goNext();
                return;
            }
        }
        if (!preSelectQuestion && questions.length >= 1) {
            goNext();
            return;
        }
        setSelectedQuestionIndex(0);
    };
    const getHelp = (question) => {
        var _a, _b;
        if (!question) {
            return '';
        }
        const helpMapper = {
            MULTIPLE_CHOICE_TYPE: t('TAKE_EXAM.MULTIPLE_CHOICE_TYPE'),
            FILL_BLANKS_TYPE: t('TAKE_EXAM.FILL_BLANKS_TYPE'),
        };
        return (_b = helpMapper[(_a = question === null || question === void 0 ? void 0 : question.questionType) === null || _a === void 0 ? void 0 : _a.typeName]) !== null && _b !== void 0 ? _b : '';
    };
    const saveQuestion = (questionToSave, updateFlag = false, redirect) => __awaiter(void 0, void 0, void 0, function* () {
        var _k, _l, _m, _o;
        if (!questionToSave || (!questionIsDirt && !updateFlag)) {
            return;
        }
        setSaving(true);
        const fields = {
            userId: getUserIdFromToken(),
            examId: getExamIdFromToken(),
            examAttemptId: examAttemptData === null || examAttemptData === void 0 ? void 0 : examAttemptData.examAttempt.id,
            questionId: questionToSave.id,
            rawAnswer: (_l = (_k = questionToSave.questionAnswer) === null || _k === void 0 ? void 0 : _k.rawAnswer) !== null && _l !== void 0 ? _l : '{}',
            flagged: (_o = (_m = questionToSave.questionAnswer) === null || _m === void 0 ? void 0 : _m.flagged) !== null && _o !== void 0 ? _o : false,
        };
        upsertQuestionAnswer({
            variables: {
                fields,
            },
        }).then(() => {
            refetch({
                variables: {
                    userId: getUserIdFromToken(),
                    examId: getExamIdFromToken(),
                },
                fetchPolicy: 'no-cache',
            });
            setQuestionIsDirt(false);
            setSaving(false);
            setMarking(false);
            redirect && navigate(redirect);
        });
        return;
    });
    const toggleFlag = (question) => {
        var _a, _b;
        setMarking(true);
        saveQuestion(Object.assign(Object.assign({}, question), { questionAnswer: Object.assign(Object.assign({}, question.questionAnswer), { flagged: (_b = !((_a = question.questionAnswer) === null || _a === void 0 ? void 0 : _a.flagged)) !== null && _b !== void 0 ? _b : true }) }), true);
    };
    const previousQuestion = (question) => __awaiter(void 0, void 0, void 0, function* () {
        if (selectedQuestionIndex == undefined) {
            return;
        }
        window.scrollTo(0, 0);
        if (question) {
            const actualQuestion = questions[selectedQuestionIndex];
            yield saveQuestion(actualQuestion);
            navigate(`/take-exam?question=${question.id.toString()}`);
            return;
        }
        const newIndex = selectedQuestionIndex - 1;
        const newQuestion = questions[newIndex];
        if (!newQuestion) {
            return;
        }
        setShowExpand(false);
        yield saveQuestion(questions[selectedQuestionIndex]);
        navigate(`/take-exam?question=${newQuestion.id.toString()}`);
    });
    const isQuestionAnswered = (actualQuestion) => {
        var _a, _b;
        const rawAnswer = ((_a = actualQuestion.questionAnswer) === null || _a === void 0 ? void 0 : _a.rawAnswer)
            ? JSON.parse((_b = actualQuestion.questionAnswer) === null || _b === void 0 ? void 0 : _b.rawAnswer)
            : undefined;
        if (!rawAnswer || (rawAnswer === null || rawAnswer === void 0 ? void 0 : rawAnswer.userAnswer) === '') {
            return false;
        }
        return true;
    };
    const isExamReadyToBeSent = () => {
        setShowConfirmationModal(true);
    };
    const finishExam = () => __awaiter(void 0, void 0, void 0, function* () {
        var _p;
        Store.removeAllNotifications();
        if (selectedQuestionIndex == undefined) {
            return;
        }
        const actualQuestion = questions[selectedQuestionIndex];
        if (!((_p = exam === null || exam === void 0 ? void 0 : exam.examConfigurations) === null || _p === void 0 ? void 0 : _p.allowGoBack) && actualQuestion.required && !isQuestionAnswered(actualQuestion)) {
            showNotification('error', t('REVIEW.MISSING_REQUIRED_QUESTIONS'));
            return;
        }
        if (questionIsDirt) {
            yield saveQuestion(actualQuestion);
        }
        yield submitExam();
    });
    const nextQuestion = (question) => __awaiter(void 0, void 0, void 0, function* () {
        var _q, _r;
        Store.removeAllNotifications();
        if (selectedQuestionIndex == undefined) {
            return;
        }
        window.scrollTo(0, 0);
        if (question) {
            const actualQuestion = questions[selectedQuestionIndex];
            if (((_q = exam === null || exam === void 0 ? void 0 : exam.examConfigurations) === null || _q === void 0 ? void 0 : _q.allowGoBack) === false &&
                actualQuestion.required === true &&
                isQuestionAnswered(actualQuestion) === false) {
                showNotification('error', t('REVIEW.MISSING_REQUIRED_QUESTIONS'));
                return;
            }
            yield saveQuestion(actualQuestion);
            navigate(`/take-exam?question=${question.id.toString()}`, {
                replace: !((_r = exam === null || exam === void 0 ? void 0 : exam.examConfigurations) === null || _r === void 0 ? void 0 : _r.allowGoBack) ? true : false,
            });
            return;
        }
        if (!question) {
            const newIndex = selectedQuestionIndex + 1;
            const restQuestions = questions.slice(newIndex);
            const next = restQuestions.find((q) => { var _a; return !((_a = q.questionAnswer) === null || _a === void 0 ? void 0 : _a.answered); });
            if (next) {
                nextQuestion(next);
            }
            else {
                const newQuestion = questions[newIndex];
                if (!newQuestion) {
                    return;
                }
                setShowExpand(false);
                yield saveQuestion(questions[selectedQuestionIndex]);
                navigate(`/take-exam?question=${newQuestion.id.toString()}`);
                return;
            }
        }
    });
    const goToReview = () => {
        if (selectedQuestionIndex == undefined) {
            return;
        }
        if (questionIsDirt) {
            saveQuestion(questions[selectedQuestionIndex], undefined, '/review');
        }
        else {
            navigate('/review');
        }
        return;
    };
    const getIndex = (questionToFind) => {
        return questions.findIndex(question => question.id === questionToFind.id);
    };
    const alterQuestions = (questionToAlter) => {
        const newquestions = questions.map(question => question.id === questionToAlter.id ? questionToAlter : question);
        setQuestions(newquestions);
        setQuestionIsDirt(true);
    };
    return (_jsxs(_Fragment, { children: [showExtendedMenu && (_jsx("div", Object.assign({ className: "container--content container--fixed" }, { children: _jsx(ReviewPage, { onGoBack: () => setShowExtendedMenu(false) }, void 0) }), void 0)), !showExtendedMenu && (_jsxs("div", Object.assign({ className: 'container--with-sidenav container--fixed-height' }, { children: [loading && _jsx(Loader, { isFullWith: true }, void 0), exam && ((_a = exam.examConfigurations) === null || _a === void 0 ? void 0 : _a.navbarActive) && examAttemptData && selectedQuestionIndex != undefined && (_jsx(ExamSidebar, { exam: exam, examAttempt: examAttemptData.examAttempt, questions: filterDisplayOnExamNavigation(questions), selectedQuestion: questions[selectedQuestionIndex], changeSelected: nextQuestion, onExtendedMenu: () => setShowExtendedMenu(true) }, void 0)), _jsx("div", Object.assign({ className: !((_b = exam === null || exam === void 0 ? void 0 : exam.examConfigurations) === null || _b === void 0 ? void 0 : _b.navbarActive)
                            ? 'container--content container--fullwidth'
                            : showSideBar
                                ? 'container--content'
                                : 'container--content container--fullwidth' }, { children: exam && selectedQuestionIndex != undefined && (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: "heading heading--desc-with-button" }, { children: [_jsxs("div", Object.assign({ className: "heading__subtitle" }, { children: [!isMiddleScreen(questions[selectedQuestionIndex]) && (_jsxs(_Fragment, { children: [_jsxs("span", { children: [" ", `${t('GENERAL.QUESTION')} ${getQuestionIndex(questions, selectedQuestionIndex)} ${t('TAKE_EXAM.OF')} ${getQuestionCount(questions)}`] }, void 0), ((_c = exam.examConfigurations) === null || _c === void 0 ? void 0 : _c.allowGoBack) && (_jsxs(_Fragment, { children: [marking && (_jsx(Button, { icon: faFlag, className: [ButtonStyle.Icons], loading: true, disabled: true, title: '' }, void 0)), !marking && ((_d = questions[selectedQuestionIndex].questionAnswer) === null || _d === void 0 ? void 0 : _d.flagged) && (_jsx(Button, { title: t('TAKE_EXAM.UNMARK_QUESTION'), icon: faFlag, className: [ButtonStyle.Icons], onClick: () => toggleFlag(questions[selectedQuestionIndex]) }, void 0)), !marking && !((_e = questions[selectedQuestionIndex].questionAnswer) === null || _e === void 0 ? void 0 : _e.flagged) && (_jsx(Button, { title: t('TAKE_EXAM.MARK_QUESTION'), icon: faFlag, className: [ButtonStyle.Icons, ButtonStyle.Secondary, ButtonStyle.Outline], onClick: () => toggleFlag(questions[selectedQuestionIndex]) }, void 0))] }, void 0))] }, void 0)), !showSideBar || !((_f = exam.examConfigurations) === null || _f === void 0 ? void 0 : _f.navbarActive) && (_jsx(Timer, { startedAt: examAttemptData === null || examAttemptData === void 0 ? void 0 : examAttemptData.examAttempt.startedAt, durationUnit: exam.durationUnit, durationTime: exam.durationTime, closingAt: exam.closingAt }, void 0))] }), void 0), _jsxs("h2", { children: [getQuestionIndex(questions, selectedQuestionIndex, '.'), " ", (_g = questions[selectedQuestionIndex]) === null || _g === void 0 ? void 0 : _g.title, "\u00A0", questions[selectedQuestionIndex].required && _jsx("i", Object.assign({ className: "text-danger" }, { children: "*" }), void 0)] }, void 0), questions[selectedQuestionIndex].description && (_jsxs(_Fragment, { children: [!isMiddleScreen(questions[selectedQuestionIndex]) && (_jsx("div", Object.assign({ className: "rich-description" }, { children: _jsxs("div", Object.assign({ className: "rich-description__iframe-container" }, { children: [_jsx(Button, { icon: showExpand ? faCompressAlt : faExpandAlt, className: [ButtonStyle.ExpandIframe, ButtonStyle.Primary], title: '', onClick: () => setShowExpand(!showExpand) }, void 0), _jsx(RichTextViewer, { id: `question-${selectedQuestionIndex}-description`, content: questions[selectedQuestionIndex].description || '', height: !showExpand ? 200 : undefined }, void 0)] }), void 0) }), void 0)), isMiddleScreen(questions[selectedQuestionIndex]) && (_jsx(RichTextViewer, { id: `question-${selectedQuestionIndex}-description`, content: questions[selectedQuestionIndex].description || '' }, void 0))] }, void 0)), getHelp(questions[selectedQuestionIndex]) && (_jsx("p", Object.assign({ className: "heading__description" }, { children: getHelp(questions[selectedQuestionIndex]) }), void 0))] }), void 0), _jsx(QuestionDrawer, { question: questions[selectedQuestionIndex], saveQuestion: (question) => alterQuestions(question), saveAudioQuestion: saveQuestion }, void 0), _jsxs("div", Object.assign({ className: "button-group" }, { children: [selectedQuestionIndex !== 0 && ((_h = exam.examConfigurations) === null || _h === void 0 ? void 0 : _h.allowGoBack) && (_jsx(Button, { className: [ButtonStyle.Secondary, ButtonStyle.Outline], title: t('GENERAL.PREVIOUS'), onClick: () => previousQuestion() }, void 0)), selectedQuestionIndex !== questions.length - 1 && (_jsx(Button, { title: t('GENERAL.NEXT'), onClick: () => nextQuestion() }, void 0)), selectedQuestionIndex === questions.length - 1 && (_jsx(_Fragment, { children: ((_j = exam.examConfigurations) === null || _j === void 0 ? void 0 : _j.allowGoBack) ? (_jsx(Button, { loading: loadingUpsertQuestionAnswer, title: t('REVIEW.CONTINUE_TO_REVIEW'), onClick: () => goToReview() }, void 0)) : (_jsx(Button, { loading: isSubmitting, title: t('REVIEW.SUBMIT_EXAM'), onClick: () => isExamReadyToBeSent() }, void 0)) }, void 0))] }), void 0)] }, void 0)) }), void 0)] }), void 0)), showConfirmationModal && (_jsx(ConfirmationModal, { title: t('REVIEW.ARE_YOU_SURE'), onConfirm: () => finishExam(), confirmText: t('GENERAL.CONFIRM'), onCancel: () => setShowConfirmationModal(false), cancelText: t('GENERAL.BUTTON_CANCEL') }, void 0))] }, void 0));
};
export default QuestionBuilder;
